import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { FullSession } from "../types"

import { useParserSearchStore } from "@/stores/parser-search-store"

import { useAIParserSession } from "./use-ai-parser-session"
import { useGetFullSession } from "./use-get-full-session"
import { useRunParserMutation } from "./use-run-parser-mutation"
import { createBaseColumns, createCustomColumns, type TableRow } from "../components/table-columns"

const EMPTY_ROWS_COUNT = 20 as const

export function useTableTransformations() {
  const { t } = useTranslation()
  const [tableData, setTableData] = useState<TableRow[]>(() => createEmptyRows(EMPTY_ROWS_COUNT))

  const { currentSessionId } = useAIParserSession()
  const { isRunParserPending } = useRunParserMutation()
  const getFullSessionQuery = useGetFullSession({})
  const searchTerm = useParserSearchStore((state) => state.searchTerm)

  const columns = useMemo(
    () => getFullSessionQuery.data?.parser_data.columns ?? [],
    [getFullSessionQuery.data]
  )

  const files = useMemo(
    () => getFullSessionQuery.data?.parser_data.files ?? [],
    [getFullSessionQuery.data]
  )

  const filteredFiles = useMemo(() => {
    if (!searchTerm.trim()) {
      return files
    }

    const lowerSearchTerm = searchTerm.toLowerCase()

    return files.filter((file) => {
      if (file.filename.toLowerCase().includes(lowerSearchTerm)) {
        return true
      }

      if (file.extracted_values) {
        return Object.values(file.extracted_values).some((value) =>
          String(value).toLowerCase().includes(lowerSearchTerm)
        )
      }

      return false
    })
  }, [files, searchTerm])

  useEffect(() => {
    if (filteredFiles.length > 0) {
      const transformedData = transformParserDataToTableRows(filteredFiles)
      setTableData(transformedData)
    } else if (files.length > 0 && searchTerm.trim()) {
      setTableData([])
    } else {
      setTableData(createEmptyRows(EMPTY_ROWS_COUNT))
    }
  }, [filteredFiles, files.length, searchTerm])

  const tableColumns = useMemo(() => {
    const baseColumns = createBaseColumns({
      t,
      isRunParserPending,
      currentSessionId,
      searchTerm,
    })
    const customColumns = createCustomColumns({
      columns,
      files: filteredFiles,
      t,
      isRunParserPending,
      searchTerm,
    })

    return [...baseColumns, ...customColumns]
  }, [columns, filteredFiles, t, isRunParserPending, currentSessionId, searchTerm])

  return {
    tableColumns,
    tableData,
    columns,
    files,
    getFullSessionQuery,
  }
}

function transformParserDataToTableRows(files: FullSession["parser_data"]["files"]): TableRow[] {
  if (!files) {
    return []
  }

  const transformedData = files.map((file) => {
    const row: TableRow = {
      documentName: file.filename,
      wasTruncated: file.was_truncated,
    }

    if (file.extracted_values) {
      Object.entries(file.extracted_values).forEach(([columnId, value]) => {
        row[columnId] = value
      })
    }

    return row
  })

  return transformedData.concat(createEmptyRows(EMPTY_ROWS_COUNT - transformedData.length))
}

function createEmptyRows(count: number): TableRow[] {
  return Array.from({ length: count }, () => ({
    documentName: null,
  }))
}
